import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type TabType = 'login' | 'register';
type LoginMode = 'password' | 'code';

// 低饱和蓝紫色系色彩常量
const C = {
  primary: '#B5C6E0',      // 蓝紫主色
  primaryDeep: '#A8B8D8',  // 蓝紫深变体
  accent: '#C5B9D8',       // 辅助淡紫
  gray: '#8E8E93',         // 苹果风格辅助灰
  bg: '#F5F7FA',           // 全局背景
  text: '#2D2B3D',
  divider: '#E5E5EA',
  dividerActive: '#B5C6E0',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabType>('login');
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regCode, setRegCode] = useState('');
  const [regName, setRegName] = useState('');
  const [remember, setRemember] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [regCountdown, setRegCountdown] = useState(0);
  const [breathe, setBreathe] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBreathe(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  useEffect(() => {
    if (regCountdown > 0) {
      const t = setTimeout(() => setRegCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [regCountdown]);

  const handleGetCode = () => { if (countdown === 0 && phone) setCountdown(60); };
  const handleRegGetCode = () => { if (regCountdown === 0 && regPhone) setRegCountdown(60); };
  const canLogin = loginMode === 'password' ? (phone && password) : (phone && code);
  const canRegister = regPhone && regPassword && regCode && regName;
  const handleLogin = () => { if (canLogin) navigate('/'); };
  const handleRegister = () => { if (canRegister) navigate('/'); };

  return (
    <div
      className="h-full flex flex-col relative overflow-hidden"
      style={{ background: C.bg, fontFamily: '\'Noto Sans SC\', sans-serif' }}
    >
      {/* 背景弥散光晕 — 蓝紫色 15% 透明度 */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: 'absolute', top: '8%', left: '15%', width: 220, height: 220, borderRadius: '50%', background: 'rgba(181,198,224,0.15)', filter: 'blur(70px)' }} />
        <div style={{ position: 'absolute', top: '35%', right: '8%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(197,185,216,0.12)', filter: 'blur(55px)' }} />
        <div style={{ position: 'absolute', bottom: '18%', left: '25%', width: 200, height: 200, borderRadius: '50%', background: 'rgba(181,198,224,0.1)', filter: 'blur(65px)' }} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden z-10">
      {/* 品牌区 — 占上 1/3 */}
      <div className="flex flex-col items-center justify-center" style={{ flex: '0 0 33%' }}>
        <div
          className="transition-all duration-1000"
          style={{ transform: breathe ? 'scale(1)' : 'scale(0.85)', opacity: breathe ? 1 : 0 }}
        >
          {/* 第五版 Logo */}
          <img
            src="https://public.readdy.ai/ai/img_res/57e672ec-ca18-4086-9dfa-c077778cd419.png"
            alt="轻醒 Logo"
            className="w-16 h-16 object-contain"
          />
        </div>
        <h1
          className="mt-3 font-semibold tracking-widest"
          style={{ fontSize: 26, color: C.primaryDeep, letterSpacing: 6, fontFamily: '\'Noto Sans SC\', sans-serif' }}
        >
          轻醒
        </h1>
        <p className="mt-1 tracking-wider" style={{ fontSize: 12, color: C.gray, letterSpacing: 2 }}>
          每天轻盈觉醒一点点
        </p>
      </div>

      {/* 登录区 — 占下 2/3，可滚动 */}
      <div className="flex-1 overflow-y-auto flex flex-col items-center px-5 pb-6" style={{ scrollbarWidth: 'none' }}>
      {/* Tab 切换 */}
      <div className="flex gap-10 mb-5">
        {(['login', 'register'] as TabType[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="relative pb-2 text-base cursor-pointer transition-colors whitespace-nowrap"
            style={{ color: tab === t ? C.primaryDeep : C.gray, fontWeight: tab === t ? 600 : 400 }}
          >
            {t === 'login' ? '登录' : '注册'}
            {tab === t && (
              <span
                className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
                style={{ width: 24, height: 2.5, background: 'linear-gradient(90deg,#B5C6E0,#C5B9D8)', display: 'block' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* 液态玻璃卡片 — 蓝紫弥散边缘 */}
      <div
        className="w-full max-w-sm rounded-3xl p-6"
        style={{
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(181,198,224,0.25)',
          boxShadow: '0 4px 40px rgba(181,198,224,0.18), inset 0 1px 0 rgba(255,255,255,0.85)',
        }}
      >
        {tab === 'login' ? (
          <LoginForm
            phone={phone} setPhone={setPhone}
            password={password} setPassword={setPassword}
            code={code} setCode={setCode}
            loginMode={loginMode} setLoginMode={setLoginMode}
            remember={remember} setRemember={setRemember}
            countdown={countdown} handleGetCode={handleGetCode}
            canLogin={!!canLogin} handleLogin={handleLogin}
          />
        ) : (
          <RegisterForm
            regPhone={regPhone} setRegPhone={setRegPhone}
            regPassword={regPassword} setRegPassword={setRegPassword}
            regCode={regCode} setRegCode={setRegCode}
            regName={regName} setRegName={setRegName}
            regCountdown={regCountdown} handleRegGetCode={handleRegGetCode}
            canRegister={!!canRegister} handleRegister={handleRegister}
          />
        )}
      </div>

      {/* 第三方登录 */}
      <div className="w-full max-w-sm mt-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px" style={{ background: 'rgba(181,198,224,0.3)' }} />
          <span style={{ fontSize: 12, color: C.gray }}>或</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(181,198,224,0.3)' }} />
        </div>
        <div className="flex justify-center gap-6">
          {[
            { icon: 'ri-wechat-line', label: '微信' },
            { icon: 'ri-apple-line', label: 'Apple' },
            { icon: 'ri-google-line', label: 'Google' },
          ].map(item => (
            <button key={item.label} className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-95 transition-transform">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(181,198,224,0.35)' }}
              >
                <i className={`${item.icon} text-xl`} style={{ color: C.primaryDeep }} />
              </div>
              <span style={{ fontSize: 11, color: C.gray }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 协议区 */}
      <p className="mt-5 text-center" style={{ fontSize: 11, color: C.gray }}>
        登录即代表同意
        <span className="cursor-pointer" style={{ color: C.primaryDeep }}>《用户协议》</span>
        和
        <span className="cursor-pointer" style={{ color: C.primaryDeep }}>《隐私政策》</span>
      </p>
      </div>{/* 登录区结束 */}
      </div>{/* flex-1 结束 */}
    </div>
  );
}

interface LoginFormProps {
  phone: string; setPhone: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  code: string; setCode: (v: string) => void;
  loginMode: LoginMode; setLoginMode: (v: LoginMode) => void;
  remember: boolean; setRemember: (v: boolean) => void;
  countdown: number; handleGetCode: () => void;
  canLogin: boolean; handleLogin: () => void;
}

function LoginForm({ phone, setPhone, password, setPassword, code, setCode, loginMode, setLoginMode, remember, setRemember, countdown, handleGetCode, canLogin, handleLogin }: LoginFormProps) {
  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-center gap-3 py-3.5" style={{ borderBottom: '1px solid #E5E5EA' }}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-smartphone-line text-base" style={{ color: '#C5B9D8' }} />
        </div>
        <input
          className="flex-1 bg-transparent outline-none"
          style={{ color: '#2D2B3D', fontSize: 15, fontFamily: '\'Noto Sans SC\', sans-serif' }}
          placeholder="请输入手机号"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          type="tel"
        />
      </div>
      <div className="flex items-center gap-3 py-3.5" style={{ borderBottom: '1px solid #E5E5EA' }}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${loginMode === 'password' ? 'ri-lock-line' : 'ri-shield-keyhole-line'} text-base`} style={{ color: '#C5B9D8' }} />
        </div>
        <input
          className="flex-1 bg-transparent outline-none"
          style={{ color: '#2D2B3D', fontSize: 15, fontFamily: '\'Noto Sans SC\', sans-serif' }}
          placeholder={loginMode === 'password' ? '请输入密码' : '请输入验证码'}
          value={loginMode === 'password' ? password : code}
          onChange={e => loginMode === 'password' ? setPassword(e.target.value) : setCode(e.target.value)}
          type={loginMode === 'password' ? 'password' : 'text'}
        />
        {loginMode === 'code' && (
          <button
            onClick={handleGetCode}
            disabled={countdown > 0 || !phone}
            className="whitespace-nowrap cursor-pointer"
            style={{ color: countdown > 0 || !phone ? '#B0B0B0' : '#A8B8D8', fontSize: 13 }}
          >
            {countdown > 0 ? `${countdown}s后重试` : '获取验证码'}
          </button>
        )}
      </div>
      <div className="flex items-center justify-between mt-4 mb-5">
        <button onClick={() => setRemember(!remember)} className="flex items-center gap-2 cursor-pointer">
          <div
            className="w-4 h-4 rounded flex items-center justify-center transition-all"
            style={{
              border: remember ? 'none' : '1.5px solid #C5B9D8',
              background: remember ? 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' : 'transparent',
            }}
          >
            {remember && <i className="ri-check-line text-white" style={{ fontSize: 10 }} />}
          </div>
          <span style={{ fontSize: 13, color: '#8E8E93' }}>记住密码</span>
        </button>
        <button className="cursor-pointer whitespace-nowrap" style={{ fontSize: 13, color: '#A8B8D8' }}>忘记密码？</button>
      </div>
      <button
        onClick={handleLogin}
        disabled={!canLogin}
        className="w-full rounded-2xl font-semibold text-white transition-all active:scale-95 whitespace-nowrap cursor-pointer"
        style={{
          height: 52,
          background: canLogin ? 'linear-gradient(135deg,#B5C6E0,#A8B8D8)' : 'rgba(181,198,224,0.35)',
          fontSize: 16,
          letterSpacing: 3,
          fontFamily: '\'Noto Sans SC\', sans-serif',
          boxShadow: canLogin ? '0 8px 24px rgba(181,198,224,0.45)' : 'none',
        }}
      >
        登录
      </button>
      <button
        onClick={() => setLoginMode(loginMode === 'password' ? 'code' : 'password')}
        className="mt-3 text-center cursor-pointer"
        style={{ fontSize: 13, color: '#A8B8D8' }}
      >
        {loginMode === 'password' ? '使用验证码登录' : '使用密码登录'}
      </button>
    </div>
  );
}

interface RegisterFormProps {
  regPhone: string; setRegPhone: (v: string) => void;
  regPassword: string; setRegPassword: (v: string) => void;
  regCode: string; setRegCode: (v: string) => void;
  regName: string; setRegName: (v: string) => void;
  regCountdown: number; handleRegGetCode: () => void;
  canRegister: boolean; handleRegister: () => void;
}

function RegisterForm({ regPhone, setRegPhone, regPassword, setRegPassword, regCode, setRegCode, regName, setRegName, regCountdown, handleRegGetCode, canRegister, handleRegister }: RegisterFormProps) {
  const inputStyle = { color: '#2D2B3D', fontSize: 15, fontFamily: '\'Noto Sans SC\', sans-serif' };
  const borderStyle = { borderBottom: '1px solid #E5E5EA' };
  return (
    <div className="flex flex-col gap-0">
      {[
        { icon: 'ri-user-line', placeholder: '请输入昵称', value: regName, onChange: setRegName, type: 'text' },
        { icon: 'ri-smartphone-line', placeholder: '请输入手机号', value: regPhone, onChange: setRegPhone, type: 'tel' },
      ].map((f, i) => (
        <div key={i} className="flex items-center gap-3 py-3.5" style={borderStyle}>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${f.icon} text-base`} style={{ color: '#C5B9D8' }} />
          </div>
          <input className="flex-1 bg-transparent outline-none" style={inputStyle} placeholder={f.placeholder} value={f.value} onChange={e => f.onChange(e.target.value)} type={f.type} />
        </div>
      ))}
      <div className="flex items-center gap-3 py-3.5" style={borderStyle}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-shield-keyhole-line text-base" style={{ color: '#C5B9D8' }} />
        </div>
        <input className="flex-1 bg-transparent outline-none" style={inputStyle} placeholder="请输入验证码" value={regCode} onChange={e => setRegCode(e.target.value)} />
        <button onClick={handleRegGetCode} disabled={regCountdown > 0 || !regPhone} className="whitespace-nowrap cursor-pointer" style={{ color: regCountdown > 0 || !regPhone ? '#B0B0B0' : '#A8B8D8', fontSize: 13 }}>
          {regCountdown > 0 ? `${regCountdown}s后重试` : '获取验证码'}
        </button>
      </div>
      <div className="flex items-center gap-3 py-3.5 mb-5" style={borderStyle}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-lock-line text-base" style={{ color: '#C5B9D8' }} />
        </div>
        <input className="flex-1 bg-transparent outline-none" style={inputStyle} placeholder="请设置密码" value={regPassword} onChange={e => setRegPassword(e.target.value)} type="password" />
      </div>
      <button
        onClick={handleRegister}
        disabled={!canRegister}
        className="w-full rounded-2xl font-semibold text-white transition-all active:scale-95 whitespace-nowrap cursor-pointer"
        style={{
          height: 52,
          background: canRegister ? 'linear-gradient(135deg,#B5C6E0,#A8B8D8)' : 'rgba(181,198,224,0.35)',
          fontSize: 16,
          letterSpacing: 3,
          fontFamily: '\'Noto Sans SC\', sans-serif',
          boxShadow: canRegister ? '0 8px 24px rgba(181,198,224,0.45)' : 'none',
        }}
      >
        注册
      </button>
    </div>
  );
}
